import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { MultiAPILink } from '@habx/apollo-multi-endpoint-link';

import { COGNITO_KEYS, COGNITO_ENV_ADMIN, COGNITO_CLIENT_ID_HEADER } from '@uptime/shared/constants/cognito';

import { logout } from '../common/utils';

const AUTH_HEADER = 'p-auth-token';

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (message === 'Context creation failed: jwt expired') {
        logout();
      }
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const userLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_USER,
});

const facilityLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FACILITY,
});

const fileLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FILE,
});

const flowLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FLOW,
});

const newDeviceLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_NEW_DEVICE,
});

const goCheckItLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_GOCHECKIT,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(COGNITO_KEYS(COGNITO_ENV_ADMIN).ID_TOKEN);
  const usedClientId = localStorage.getItem(COGNITO_KEYS(COGNITO_ENV_ADMIN).CLIENT_ID);
  const clientHeader = usedClientId ? { [COGNITO_CLIENT_ID_HEADER]: usedClientId } : {};

  return {
    headers: {
      ...headers,
      [AUTH_HEADER]: token,
      ...clientHeader,
    },
  };
});

const multiApiLink = new MultiAPILink({
  endpoints: {
    file: import.meta.env.VITE_GRAPHQL_FILE,
    user: import.meta.env.VITE_GRAPHQL_USER,
    facility: import.meta.env.VITE_GRAPHQL_FACILITY,
    flow: import.meta.env.VITE_GRAPHQL_FLOW,
    newdevice: import.meta.env.VITE_GRAPHQL_NEW_DEVICE,
    device: import.meta.env.VITE_GRAPHQL_DEVICE,
    gocheckit: import.meta.env.VITE_GRAPHQL_GOCHECKIT,
    marketplace: import.meta.env.VITE_GRAPHQL_MARKETPLACE,
    iot: import.meta.env.VITE_GRAPHQL_IOT,
  },
  createHttpLink,
  httpSuffix: '',
});

const cache = new InMemoryCache();

const isDevToolsEnabled = !import.meta.env.PROD;

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(multiApiLink)]),
  cache: new InMemoryCache({ addTypename: false }),
});

export const userClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(userLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'user',
  },
});

export const facilityClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(facilityLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'facility',
  },
});

export const fileClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(fileLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'file',
  },
});

export const flowClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(flowLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'flow',
  },
});

export const newDeviceClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(newDeviceLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'newDevice',
  },
});

export const goCheckItClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(goCheckItLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'goCheckIt',
  },
});
